// ─── PICKUP RESOLUTION ────────────────────────────────────────────────────────
// Resolves each child's drop-off/pickup assignments for a given date.
//
// A child can have up to 3 named routines (School, Aftercare, …), each with an
// optional drop-off leg and an optional pickup leg. These are stored per-child
// as `pickup_routines` in Settings → Family Setup → Children:
//
//   pickup_routines: [{
//     id:      'rt_1',
//     label:   'School',
//     days:    ['Mo','Tu','We','Th','Fr'],
//     dropoff: <leg> | null,
//     pickup:  <leg> | null,
//   }]
//
// where a <leg> is:
//   { enabled, time, adult_pattern, adult_all_week, adult_mwf, adult_tth, adult_days }
//   adult_pattern: 'all_week' | 'mwf_tth' | 'specific'
//   adult ids are a care partner identifier or 'cg:{sitterId}'.
//
// These legs are injected into the app as forced reminder + assignment rows —
// they are never written to Google Calendar.
//
// BACKWARD COMPATIBILITY: children saved before routines existed carry flat
// pickup_* fields (pickup_enabled/days/time/adult_*/label). getRoutines()
// synthesises a single 'rt_legacy' routine (pickup leg only) from them, so no
// data migration is needed — legacy children resolve exactly as before.
//
// An unset adult resolves to adultId: null — surfaced as an "unset" badge.
//
// Per-day overrides live in Firestore at families/{id}/pickupOverrides/{key},
// where key = overrideKey(childId, date, routineId, leg):
//   { childId, date, routineId, leg, adultId: 'A'|'cg:{id}'|'skip'|'unassigned', actorId, actorName }
// The legacy routine's pickup leg keeps the old `${childId}_${date}` key so
// overrides set before this shipped keep matching.
// 'skip' suppresses the leg for that day (adultId: null, skipped: true).
// 'unassigned' keeps it visible but clears the adult (adultId: null, skipped: false).
//
// familyData.config.pickupsActive (default true) is a family-wide seasonal
// on/off switch — pass it as pickupsForDate's `active` option to suppress
// everything (e.g. summer break) without touching per-child config.

const DAY_KEYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const DEFAULT_PICKUP_DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr'];
export const MAX_ROUTINES = 3;
const LEGACY_ROUTINE_ID = 'rt_legacy';

// 'Mo'..'Su' for a yyyy-mm-dd string
export function dayKeyForDate(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  if (isNaN(d)) return null;
  return DAY_KEYS[d.getDay()];
}

// Which adult id covers a single leg on the given weekday — null if unset.
export function legAdultForDay(leg, dayKey) {
  if (!leg) return null;
  const pattern = leg.adult_pattern || 'all_week';
  if (pattern === 'all_week') return leg.adult_all_week || null;
  if (pattern === 'mwf_tth') {
    if (['Mo', 'We', 'Fr'].includes(dayKey)) return leg.adult_mwf || null;
    if (['Tu', 'Th'].includes(dayKey))       return leg.adult_tth || null;
    return null;
  }
  if (pattern === 'specific') return (leg.adult_days || {})[dayKey] || null;
  return null;
}

// Legacy single-leg helper — reads the flat pickup_* fields off a child.
// Retained for existing callers/tests; equivalent to legAdultForDay on the
// child's legacy pickup leg.
export function pickupAdultForDay(child, dayKey) {
  return legAdultForDay(legFromFlatFields(child), dayKey);
}

function legFromFlatFields(child) {
  return {
    enabled:        child.pickup_enabled !== false,
    time:           child.pickup_time || '3:15 PM',
    adult_pattern:  child.pickup_adult_pattern || 'all_week',
    adult_all_week: child.pickup_adult_all_week || '',
    adult_mwf:      child.pickup_adult_mwf || '',
    adult_tth:      child.pickup_adult_tth || '',
    adult_days:     child.pickup_adult_days || {},
  };
}

// Normalised routines for a child — real ones if present, else one synthesised
// from the legacy flat fields (pickup leg only).
export function getRoutines(child) {
  if (!child) return [];
  if (child.pickup_enabled === false) return []; // global off switch (both models)
  if (Array.isArray(child.pickup_routines) && child.pickup_routines.length) {
    return child.pickup_routines;
  }
  return [{
    id:      LEGACY_ROUTINE_ID,
    label:   child.pickup_label || 'School',
    days:    child.pickup_days?.length ? child.pickup_days : DEFAULT_PICKUP_DAYS,
    dropoff: null,
    pickup:  legFromFlatFields(child),
    _legacyLabel: child.pickup_label || 'School pickup', // preserve exact legacy display
  }];
}

// Firestore doc id / overrides-map key for one leg on one date. The legacy
// routine's pickup leg keeps the pre-routines `${childId}_${date}` key.
export function overrideKey(childId, dateStr, routineId = null, leg = null) {
  if (routineId == null || (routineId === LEGACY_ROUTINE_ID && leg === 'pickup')) {
    return `${childId}_${dateStr}`;
  }
  return `${childId}_${routineId}_${leg}_${dateStr}`;
}

function legLabel(routine, leg) {
  if (routine._legacyLabel && leg === 'pickup') return routine._legacyLabel;
  const base = routine.label || 'Pickup';
  return leg === 'dropoff' ? `${base} drop-off` : `${base} pickup`;
}

function legRow(child, routine, leg, legData, dateStr, overridesMap) {
  const dayKey = dayKeyForDate(dateStr);
  const defaultAdultId = legAdultForDay(legData, dayKey);
  const row = {
    childId:   child.id,
    childName: child.preferred_name || child.firstName || child.name || '',
    routineId: routine.id,
    leg,
    time:      legData.time || (leg === 'dropoff' ? '8:00 AM' : '3:15 PM'),
    adultId:   defaultAdultId,
    defaultAdultId,
    label:     legLabel(routine, leg),
    source:    'default',
    skipped:   false,
    date:      dateStr,
    key:       overrideKey(child.id, dateStr, routine.id, leg),
  };
  const ov = overridesMap?.[row.key];
  if (ov?.adultId === 'skip')       return { ...row, adultId: null, skipped: true,  source: 'override' };
  if (ov?.adultId === 'unassigned') return { ...row, adultId: null, skipped: false, source: 'override' };
  if (ov?.adultId)                  return { ...row, adultId: ov.adultId, source: 'override' };
  return row;
}

// All drop-off/pickup rows for one child on one date (both legs, all routines).
export function pickupEventsForChildOnDate(child, dateStr, overridesMap = null) {
  if (!child) return [];
  const dayKey = dayKeyForDate(dateStr);
  if (!dayKey) return [];
  const rows = [];
  for (const routine of getRoutines(child)) {
    const days = routine.days?.length ? routine.days : DEFAULT_PICKUP_DAYS;
    if (!days.includes(dayKey)) continue;
    if (routine.dropoff?.enabled !== false && routine.dropoff) {
      rows.push(legRow(child, routine, 'dropoff', routine.dropoff, dateStr, overridesMap));
    }
    if (routine.pickup && routine.pickup.enabled !== false) {
      rows.push(legRow(child, routine, 'pickup', routine.pickup, dateStr, overridesMap));
    }
  }
  return rows;
}

// Legacy single-row helper — the child's primary pickup row (first routine's
// pickup leg) or null. Retained for existing callers/tests.
export function pickupForChildOnDate(child, dateStr, overridesMap = null) {
  if (!child || child.pickup_enabled === false) return null;
  const rows = pickupEventsForChildOnDate(child, dateStr, overridesMap);
  return rows.find(r => r.leg === 'pickup') || null;
}

// All drop-off/pickup rows for a date across all children, sorted by time.
// `active` is the family-wide seasonal toggle (familyData.config.pickupsActive,
// default true) — when false, everything is suppressed without touching config.
export function pickupsForDate(children, dateStr, overridesMap = null, { includeSkipped = false, active = true } = {}) {
  if (!active) return [];
  return (children || [])
    .flatMap(c => pickupEventsForChildOnDate(c, dateStr, overridesMap))
    .filter(p => includeSkipped || !p.skipped);
}

// Display name for a pickup adult id. Care partners are stored by identifier;
// caregivers (Phase 5) are stored as 'cg:{sitterId}'.
export function resolvePickupAdult(adultId, { carePartners = [], sitters = [] } = {}) {
  if (!adultId) return null;
  if (adultId.startsWith('cg:')) {
    const s = sitters.find(x => x.id === adultId.slice(3));
    return s ? (s.firstName || s.name || adultId) : adultId;
  }
  const cp = carePartners.find(x => x.identifier === adultId || x.id === adultId);
  return cp ? (cp.name?.split(' ')[0] || cp.name || adultId) : adultId;
}
